import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';
import type {
  Id,
  PaymentMethodId,
  PaymentTermId,
  PrintStyleId,
  QuotationStateId,
  QuotationWorkflowId,
  ValidityPeriodId,
} from '@e-socna/data-layer';

import { useDocumentEditorContext } from '../../components/DocumentEditor/contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { PATH_NOT_FOUND } from '../../app/constants/paths.constant';
import { RightDrawer } from '../../components/RightDrawer';
import { MissionQuotationInfoForm } from '../../forms/MissionQuotationInfo';
import { Code } from '../../components/Code';
import { FormVariant } from '../../forms';
import { MissionQuotationInfoStateForm } from '../../forms/MissionQuotationInfoState';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { QuotationHeader } from '../../components/QuotationHeader';
import { useFindAllDocumentTemplatesQuery } from '../DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';

import type { MissionQuotationWithItsInfo } from './types/mission-quotation-with-its-info.type';

export const MissionQuotationInfoTabPanel = () => {
  const {
    props: {
      routerSettings: { editorModePaths },
      handlers,
    },
    state,
    actions,
  } = useDocumentEditorContext<MissionQuotationWithItsInfo>();

  const {
    paymentMethodsIdMap,
    paymentTermsIdMap,
    printStylesIdMap,
    quotationStatesIdMap,
    quotationWorkflowsIdMap,
    validityPeriodsIdMap,
  } = useReferencesContext();

  const { data: documentTemplatesData } = useFindAllDocumentTemplatesQuery();

  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();

  const { missionId } = useMissionReadyContext();

  const leaveEditionMode = useCallback(
    () => void navigate(editorModePaths.Info ?? PATH_NOT_FOUND),
    [editorModePaths.Info, navigate],
  );

  const enterStateEditionMode = useCallback(
    () => void navigate(editorModePaths.InfoEditState ?? PATH_NOT_FOUND),
    [navigate, editorModePaths.InfoEditState],
  );

  const isInStateEditionMode = currentPath === editorModePaths.InfoEditState;

  const { editable } = state;
  const {
    code,
    codingConfig,
    info,
    printing: { printSettings },
    templateId,
    title,
  } = editable;

  const useSubmitFactory = useCallback(
    (callback?: () => void) =>
      (values: Readonly<MissionQuotationWithItsInfo>) => {
        actions.updateInfo(values);
        void handlers.onSavePrintSettings?.(values.printing.printSettings);
        callback?.();
      },
    [actions, handlers],
  );

  const {
    quotationWorkflow,
    paymentMethod,
    paymentTerms,
    validityPeriod,
    printStyle,
  } = ensureDefined({
    quotationState: quotationStatesIdMap[info.stateId as QuotationStateId],
    quotationWorkflow:
      quotationWorkflowsIdMap[info.workflowId as QuotationWorkflowId],
    paymentMethod:
      paymentMethodsIdMap[info.header.paymentMethodId as PaymentMethodId],
    paymentTerms: paymentTermsIdMap[info.header.paymentTermId as PaymentTermId],
    validityPeriod:
      validityPeriodsIdMap[info.header.validityPeriodId as ValidityPeriodId],
    printStyle: printStylesIdMap[printSettings.printStyleId as PrintStyleId],
  });

  const template = documentTemplatesData?.findAllDocumentTemplates.find(
    (candidate) => candidate._id === templateId,
  );

  const templateLabel = template
    ? `${template.code} - ${template.label}`
    : 'Aucun';

  const handleSuccess = useCallback(
    ({ newStateId }: Readonly<{ actionId: Id; newStateId: Id }>) => {
      actions.updateInfo(
        {
          ...editable,
          info: {
            ...editable.info,
            stateId: newStateId,
          },
        },
        false,
      );
      actions.bumpLatestSavedVersion();
    },
    [actions, editable],
  );

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <MissionQuotationInfoStateForm
          enterEditionMode={enterStateEditionMode}
          isInEditionMode={isInStateEditionMode}
          leaveEditionMode={leaveEditionMode}
          missionId={missionId}
          missionQuotationInfo={info}
          onSuccess={handleSuccess}
          quotationId={info.quotationId ?? EMPTY_ID}
        />
        <Typography gutterBottom variant="h6">
          Entête de devis
        </Typography>
        <QuotationHeader
          interventionDate={info.header.interventionDate}
          modalities={info.header.modalities}
          note={info.header.note}
          paymentMethod={paymentMethod.label}
          paymentTerms={paymentTerms.label}
          validityPeriod={validityPeriod.label}
        />
        <Typography gutterBottom variant="h6">
          Paramétrage
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="body1">
              Workflow : {quotationWorkflow.label}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Document
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="body1">
              Modèle de document : {templateLabel}
            </Typography>
            <Code code={code} codingConfig={codingConfig} />
            <Typography gutterBottom variant="body1">
              Titre : {!title || title === EMPTY_STRING ? 'Non défini' : title}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Options d&apos;impression
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="body1">
              Inclure entêtes de pages :{' '}
              {printSettings.addHeaders ? 'Oui' : 'Non'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Inclure pieds de pages :{' '}
              {printSettings.addFooters ? 'Oui' : 'Non'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Style d&apos;impression : {printStyle.label}
            </Typography>
          </Box>
        </Paper>
      </Stack>
      <RightDrawer
        isOpen={currentPath === editorModePaths.InfoEdit}
        onDrawerClose={leaveEditionMode}
      >
        <MissionQuotationInfoForm
          formVariant={FormVariant.Modification}
          initialValues={editable}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          showCancelButton
          useSubmitFactory={useSubmitFactory}
        />
      </RightDrawer>
    </>
  );
};
